import { HASH_CACHE } from 'boot/encryptionHashCache';
import IdxDb from 'boot/indexedDb';
import { network } from 'boot/network';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { Platform } from 'quasar';
import { logEvent } from 'src/utils/LogEvent';

/*
@todo: password generation:
  https://stackoverflow.com/questions/9719570/generate-random-password-string-with-requirements-in-javascript
*/

/** @typedef {{
 * publicKeyName?: string,
 * saltName?: string,
 * saltType?: string,
 * privateKeyName?: string,
 *  decryptionKeyName?: string,
 }} encryptionKeysNames */

const ENCRYPTION_ALGORITHM = 'RSA-OAEP';
const ENCRYPTION_OBJECT_STORE = 'encryptionKeys';

const MAX_ENCRYPTION_BYTE_SIZE = 100; // it depends on the key settings, but 100 is a safe value
const HASH_ALGORITHM = 'SHA-256';

function splitStringByByteSize(string) {
  const chunkedArray = [];
  let currentChunk = '';
  let currentByteSize = 0;
  const encoder = new TextEncoder();

  // eslint-disable-next-line no-restricted-syntax
  for (const char of string) {
    const charByteSize = encoder.encode(char).byteLength;

    if (currentByteSize + charByteSize <= MAX_ENCRYPTION_BYTE_SIZE) {
      currentChunk += char;
      currentByteSize += charByteSize;
    } else {
      chunkedArray.push(currentChunk);
      currentChunk = char;
      currentByteSize = charByteSize;
    }
  }

  if (currentChunk !== '') {
    chunkedArray.push(currentChunk);
  }

  return chunkedArray;
}

function arrayBufferToString(buffer) {
  const decoder = new TextDecoder('utf-8');
  return decoder.decode(buffer);
}

/**
 * @param {string} string - value to be hashed
 * @param {number | null} initValue - value to be hashed instead of {@link string} value
 *  (if we need to keep its type)
 * @return {Promise<ArrayBuffer>}
 * */
async function storeHashFromString(string, initValue = null) {
  const encoder = new TextEncoder();
  const data = encoder.encode(string);
  const hash = await crypto.subtle.digest(HASH_ALGORITHM, data);
  const stringHash = arrayBufferToString(hash);
  HASH_CACHE[stringHash] = initValue ?? string;
  return stringHash;
}

/**
 * @param {string} id
 * @param {string} type
 * @param value
 * */
export async function addSecret(id, type, value) {
  return IdxDb[ENCRYPTION_OBJECT_STORE].put({
    id,
    type,
    value,
  });
}

export async function readSecret(id) {
  return IdxDb[ENCRYPTION_OBJECT_STORE].get(id);
}

export async function deleteSecret(id) {
  return IdxDb[ENCRYPTION_OBJECT_STORE].delete(id);
}

/**
 * @param {Uint8Array} publicExponent
 * @return {{hash: string, modulusLength: number, name: string, publicExponent: Uint8Array}}
* */
const getAsymmetricAlgorithm = (publicExponent) => {
  return {
    name: ENCRYPTION_ALGORITHM,
    modulusLength: 2048,
    publicExponent,
    hash: HASH_ALGORITHM, // Use SHA-256
  };
};

/**
 * Generates a pair of public and private keys using the Web Crypto API.
 * @return {Promise<CryptoKeyPair>}
 */
async function createAsymmetricKeyPair() {
  try {
    return await window.crypto.subtle.generateKey(
      getAsymmetricAlgorithm(new Uint8Array([1, 0, 1])),
      true,
      ['encrypt', 'decrypt'],
    );
  } catch (error) {
    console.error(`createAsymmetricKeyPair failed: ${error}`);
    logEvent(error);
    throw error;
  }
}

/**
 * @param {'salt'|'iv'} type
 * @return {Promise<Uint8Array>}
 * */
async function createAndSaveSalt(type = 'salt') {
  const salt = window.crypto.getRandomValues(new Uint8Array(16)); // Generate a random salt
  await addSecret(type, type, salt);

  return salt;
}

/**
 * @param {string} password
 * @param {Uint8Array} salt
 * @return {Promise<CryptoKey>}
 * */
async function deriveKeyFromPassword(password, salt) {
  const encoder = new TextEncoder();
  const passwordData = encoder.encode(password);

  try {
    // Derive a 256-bit (32-byte) encryption key using PBKDF2
    const passwordKey = await crypto.subtle.importKey('raw', passwordData, { name: 'PBKDF2' }, false, ['deriveKey']);
    const derivedKey = await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt,
        iterations: 250000,
        hash: HASH_ALGORITHM,
      },
      passwordKey,
      { name: 'AES-GCM', length: 256 }, // derivedKeyAlgorithm
      false, // exportable
      ['encrypt', 'decrypt', 'wrapKey', 'unwrapKey'], // usage
    );

    return derivedKey;
  } catch (err) {
    console.error('Error deriving the key from the password', err);
    logEvent(err);
    throw err;
  }
}

/**
 * @param {CryptoKey} privateKey
 * @param {CryptoKey} masterKey
 * @param {string} saltType
 * @return {Promise<ArrayBuffer>}
 * */
async function wrapPrivateKey(privateKey, masterKey, saltType = 'iv') {
  try {
    const iv = await createAndSaveSalt(saltType);
    return await window.crypto.subtle.wrapKey('jwk', privateKey, masterKey, {
      name: 'AES-GCM',
      iv,
    });
  } catch (err) {
    console.error('Error wrapping the private key', err);
    logEvent(err);
  }
}

/**
 * @param {ArrayBuffer} encryptedPrivateKey
 * @param {CryptoKey} masterKey
 * @param {Uint8Array} iv
 * @return {Promise<CryptoKey>}
 * */
async function unwrapPrivateKey(encryptedPrivateKey, masterKey, iv) {
  try {
    const unwrapped = await window.crypto.subtle.unwrapKey(
      'jwk',
      encryptedPrivateKey,
      masterKey,
      {
        name: 'AES-GCM',
        iv,
      },
      getAsymmetricAlgorithm(new Uint8Array([1, 0, 1])),
      false, // whether the key is extractable (i.e., can be exported)
      ['decrypt'], // key usages
    );

    return unwrapped;
  } catch (err) {
    console.error('Error unwrapping the private key', err);
    logEvent(err);
  }
}

/**
 * Encrypts a string using the provided public key.
 * @param {string} string - The message to be encrypted.
 * @param {CryptoKey} publicKey - The public key used for encryption.
 * @returns {Promise<ArrayBuffer>} A promise that resolves to the encrypted message as an ArrayBuffer.
 */
async function encrypt(string, publicKey) {
  const encodedMessage = new TextEncoder().encode(string);
  return crypto.subtle.encrypt(
    {
      name: ENCRYPTION_ALGORITHM,
    },
    publicKey,
    encodedMessage,
  );
}

/**
 * Encrypts a message using the provided public key. Splits string in chunk due to the encryption algorithm limitations.
 * @param {string | number} value - The message to be encrypted.
 * @param {CryptoKey} publicKey - The public key used for encryption.
 * @returns {Promise<Object | string | number | null>} A promise that resolves to the encryption object.
 */
export async function createEncryptedDataObject(value, publicKey) {
  const valueType = typeof value;
  let resultValue = value;

  if (valueType === 'number' && !Number.isNaN(value)) {
    resultValue = value.toString();
  }

  const allowedTypes = ['string', 'number'];

  // don't encrypt '', null
  if (!resultValue || !allowedTypes.includes(valueType)) {
    return value;
  }

  const encryptedString = await Promise.all(splitStringByByteSize(resultValue).map((stringChunk) => {
    return encrypt(stringChunk, publicKey);
  }));

  const resultObject = {
    value: encryptedString,
    type: valueType,
    encrypted: true,
  };

  if (network.offlineMode) {
    resultObject.hashString = await storeHashFromString(
      resultValue,
      (valueType === 'number' && !Number.isNaN(value)) ? value : null,
    );
  }

  return resultObject;
}

/**
 * Decrypts an encrypted message using the provided private key.
 * @param {Object} encryptedData - An object with encrypted data and its JS type.
 * @param {ArrayBuffer} encryptedData.value - The encrypted message.
 * @param {CryptoKey} privateKey - The private key used for decryption.
 * @returns {Promise<Object>} A promise that resolves to the decrypted message.
 */
export async function decrypt({ value }, privateKey) {
  try {
    const decryptedBuffer = await crypto.subtle.decrypt(
      {
        name: ENCRYPTION_ALGORITHM,
      },
      privateKey,
      value,
    );

    return new TextDecoder().decode(decryptedBuffer);
  } catch (err) {
    console.error('Error decrypting the message', err);
  }
}

/**
 * @param {string[]} value
 * @return {number | string}
 */
function parseDecryptedNumberValue(value) {
  // Number() does not always parse float numbers right. We should double-check it with parseFloat.
  const result = Number(value.join(''));
  return typeof result === 'string' || Number.isNaN(result)
    ? parseFloat(value.join(''))
    : result;
}

export async function decryptDataObject(encryptedData, publicKey) {
  if (!encryptedData) {
    return encryptedData;
  }

  const { value, type, encrypted, hashString } = encryptedData;

  if (hashString && HASH_CACHE[hashString]) {
    return HASH_CACHE[hashString];
  }

  let isParentObject = false;

  if (Array.isArray(value) && encrypted) {
    const res = await Promise.all(value.map((encryptedItem) => {
      if (!encrypted) {
        isParentObject = true;
        return encryptedItem;
      }

      isParentObject = false;
      return decrypt({ value: encryptedItem }, publicKey);
    }));

    if (isParentObject) {
      return res;
    }

    if (type === 'number') {
      return parseDecryptedNumberValue(res);
    }

    return res.join('');
  }

  return encryptedData;
}

export async function saveEncryptionPassword(password) {
  try {
    if (!Platform.is.capacitor) {
      return;
    }

    await SecureStoragePlugin.set({ key: 'encryptionPassword', value: password });
  } catch (err) {
    console.error('Error saving encryption password', err);
    logEvent(err);
  }
}

export async function readEncryptionPassword() {
  try {
    if (!Platform.is.capacitor) {
      return null;
    }

    const { value } = await SecureStoragePlugin.get({ key: 'encryptionPassword' });
    return value;
  } catch (err) {
    console.error('Error reading encryption password', err);
    logEvent(err);
    return null;
  }
}

/**
 * @param {string} encryptionPassword
 * @param {encryptionKeysNames} encryptionKeysNames
 */
export async function createEncryptionKeys(encryptionPassword, {
  publicKeyName = 'publicKey',
  saltName = 'salt',
  saltType = 'iv',
  privateKeyName = 'privateKey',
} = {}) {
  const keyPair = await createAsymmetricKeyPair();
  await addSecret(publicKeyName, 'key', keyPair.publicKey);
  const salt = await createAndSaveSalt(saltName);
  // @todo: probably ask user to "confirm" their password if they have one?
  //  Just use the `signInWithEmailAndPassword()`
  const masterKey = await deriveKeyFromPassword(encryptionPassword, salt);
  const encryptedPrivateKey = await wrapPrivateKey(keyPair.privateKey, masterKey, saltType);
  await addSecret(privateKeyName, 'encryptedKey', encryptedPrivateKey);
}

/**
 * @param {string} encryptionPassword
 * @param {encryptionKeysNames} encryptionKeysNames
 * @return {boolean}
 */
export async function decryptPrivateKey(encryptionPassword, {
  publicKeyName = 'publicKey',
  saltName = 'salt',
  saltType = 'iv',
  privateKeyName = 'privateKey',
  decryptionKeyName = 'decryptionKey',
} = {}) {
  const [salt, iv, encryptedPrivateKey] = await Promise.all([
    readSecret(saltName),
    readSecret(saltType),
    readSecret(privateKeyName),
  ]);
  const masterKey = await deriveKeyFromPassword(encryptionPassword, salt.value);
  const privateKey = await unwrapPrivateKey(encryptedPrivateKey.value, masterKey, iv.value);

  if (!privateKey) {
    return false;
  }

  await addSecret(decryptionKeyName, 'key', privateKey);
  return true;
}
